import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Camera, Activity, ChevronRight, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { API_URL, WOUND_TYPES } from './constants';

export function WoundsTab({ wounds, clientId, onRefresh, user }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [photoDialog, setPhotoDialog] = useState(null);
  const [timelineDialog, setTimelineDialog] = useState(null);
  const [timelinePhotos, setTimelinePhotos] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [form, setForm] = useState({ wound_type: 'pressure_ulcer', location: '', grade: '', treatment: '', dressing_type: '', notes: '', next_review_date: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    if (!form.location) return toast.error('Wound location required');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/clinical/wounds`, { ...form, client_id: clientId, grade: form.grade ? parseInt(form.grade) : null }, { withCredentials: true });
      toast.success('Wound record created');
      setDialogOpen(false);
      setForm({ wound_type: 'pressure_ulcer', location: '', grade: '', treatment: '', dressing_type: '', notes: '', next_review_date: '' });
      onRefresh();
    } catch (err) { toast.error('Failed'); }
    setSubmitting(false);
  };

  const handlePhotoUpload = async () => {
    if (!photoDialog || !photoFile) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append('photo', photoFile);
    try {
      await axios.post(`${API_URL}/api/clinical/wounds/${photoDialog.id}/photo`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      setPhotoDialog(null);
      setPhotoFile(null);
      onRefresh();
    } catch (err) { toast.error('Upload failed'); }
    setSubmitting(false);
  };

  const openTimeline = async (wound) => {
    setTimelineDialog(wound);
    try {
      const { data } = await axios.get(`${API_URL}/api/clinical/wounds/${wound.id}/photos`, { withCredentials: true });
      setTimelinePhotos(data || []);
    } catch (err) {
      setTimelinePhotos([]);
      toast.error('Failed to load photos');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 text-white" onClick={() => setDialogOpen(true)} data-testid="add-wound-btn"><Plus className="w-4 h-4 mr-1.5" />New Wound Record</Button>
      </div>

      {wounds.length === 0 ? (
        <Card className="glass-card border-border"><CardContent className="p-8 text-center"><Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No wound records</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {wounds.map(w => (
            <Card key={w.id} className="border border-[#8B5CF6]/20" data-testid={`wound-${w.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground capitalize">{w.wound_type?.replace(/_/g, ' ')} — {w.location}</p>
                    <p className="text-xs text-muted-foreground">
                      {w.grade && `Grade ${w.grade} · `}
                      {w.treatment && `Treatment: ${w.treatment} · `}
                      {w.dressing_type && `Dressing: ${w.dressing_type}`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Status: <span className={`font-medium ${w.status === 'healed' ? 'text-emerald-500' : w.status === 'worsening' ? 'text-red-500' : 'text-[#8B5CF6]'}`}>{w.status}</span>
                      {w.next_review_date && ` · Next review: ${w.next_review_date}`}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {w.recorded_by_name} ({w.staff_number || w.usic}) — {new Date(w.timestamp).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button size="sm" variant="outline" className="border-[#8B5CF6]/50 text-[#8B5CF6]" onClick={() => setPhotoDialog(w)} data-testid={`photo-${w.id}`}>
                      <Camera className="w-4 h-4 mr-1" />Add Photo
                    </Button>
                    {(w.photos?.length > 0) && (
                      <Button size="sm" variant="ghost" className="text-[#8B5CF6] text-xs" onClick={() => openTimeline(w)} data-testid={`timeline-${w.id}`}>
                        <ChevronRight className="w-3 h-3 mr-1" />{w.photos.length} photo(s)
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Wound Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-card border-border sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-foreground">New Wound Record</DialogTitle><DialogDescription className="text-muted-foreground">Document wound assessment</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-foreground">Wound Type</Label>
                <select value={form.wound_type} onChange={e => setForm(p => ({...p, wound_type: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm capitalize" data-testid="wound-type-select">
                  {WOUND_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select></div>
              <div><Label className="text-foreground">Grade (1-4)</Label>
                <select value={form.grade} onChange={e => setForm(p => ({...p, grade: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm" data-testid="wound-grade">
                  <option value="">N/A</option><option value="1">Grade 1</option><option value="2">Grade 2</option><option value="3">Grade 3</option><option value="4">Grade 4</option>
                </select></div>
            </div>
            <div><Label className="text-foreground">Body Location *</Label><Input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="e.g., Left heel, Sacrum, Right forearm" className="mt-1 bg-muted border-border text-foreground" data-testid="wound-location" /></div>
            <div><Label className="text-foreground">Treatment Applied</Label><Input value={form.treatment} onChange={e => setForm(p => ({...p, treatment: e.target.value}))} placeholder="e.g., Cleaned with saline, barrier cream applied" className="mt-1 bg-muted border-border text-foreground" /></div>
            <div><Label className="text-foreground">Dressing Type</Label><Input value={form.dressing_type} onChange={e => setForm(p => ({...p, dressing_type: e.target.value}))} placeholder="e.g., Mepilex, Aquacel, Duoderm" className="mt-1 bg-muted border-border text-foreground" /></div>
            <div><Label className="text-foreground">Next Review Date</Label><Input type="date" value={form.next_review_date} onChange={e => setForm(p => ({...p, next_review_date: e.target.value}))} className="mt-1 bg-muted border-border text-foreground" /></div>
            <div><Label className="text-foreground">Notes</Label><textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="mt-1 w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] resize-none" placeholder="Observations..." /></div>
            <Button className="w-full bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 text-white" onClick={handleAdd} disabled={submitting} data-testid="submit-wound-btn">{submitting ? 'Creating...' : 'Create & Sign'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      {photoDialog && (
        <Dialog open={!!photoDialog} onOpenChange={() => { setPhotoDialog(null); setPhotoFile(null); }}>
          <DialogContent className="glass-card border-border sm:max-w-sm">
            <DialogHeader><DialogTitle className="text-foreground">Upload Wound Photo</DialogTitle><DialogDescription className="text-muted-foreground">{photoDialog.wound_type?.replace(/_/g, ' ')} — {photoDialog.location}</DialogDescription></DialogHeader>
            <Input type="file" accept="image/*" capture="environment" onChange={e => setPhotoFile(e.target.files?.[0])} className="bg-muted border-border text-foreground" data-testid="wound-photo-input" />
            {photoFile && <p className="text-xs text-muted-foreground">{photoFile.name} ({(photoFile.size / 1024).toFixed(1)} KB)</p>}
            <Button className="w-full bg-[#8B5CF6] hover:bg-[#8B5CF6]/90 text-white" onClick={handlePhotoUpload} disabled={!photoFile || submitting} data-testid="upload-wound-photo-btn">{submitting ? 'Uploading...' : 'Upload Photo'}</Button>
          </DialogContent>
        </Dialog>
      )}

      {/* Photo Timeline Dialog */}
      {timelineDialog && (
        <Dialog open={!!timelineDialog} onOpenChange={() => setTimelineDialog(null)}>
          <DialogContent className="glass-card border-border sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Wound Healing Timeline — {timelineDialog.wound_type?.replace(/_/g, ' ')} ({timelineDialog.location})
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">Serial photo documentation showing progression</DialogDescription>
            </DialogHeader>

            {timelinePhotos.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No photos uploaded yet</p>
            ) : (
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-px bg-[#8B5CF6]/30" />

                <div className="space-y-6">
                  {timelinePhotos.map((photo, idx) => (
                    <div key={idx} className="relative flex gap-4 pl-3" data-testid={`timeline-photo-${idx}`}>
                      {/* Timeline dot */}
                      <div className="relative z-10 flex-shrink-0">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${idx === 0 ? 'bg-[#8B5CF6]' : 'bg-[#8B5CF6]/50'}`}>
                          {timelinePhotos.length - idx}
                        </div>
                      </div>

                      <div className="flex-1 bg-muted/50 rounded-xl border border-border p-3">
                        {/* Date & uploader */}
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-foreground">
                            {new Date(photo.timestamp).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            <span className="text-muted-foreground ml-2">{new Date(photo.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {photo.uploaded_by_name} ({photo.staff_number || ''})
                          </p>
                        </div>

                        {/* Photo */}
                        {photo.data && (
                          <img
                            src={photo.data}
                            alt={`Wound photo ${timelinePhotos.length - idx}`}
                            className="w-full max-h-64 object-contain rounded-lg bg-black/20"
                          />
                        )}

                        {/* Notes */}
                        {photo.notes && <p className="text-xs text-muted-foreground mt-2">{photo.notes}</p>}

                        {/* Day indicator */}
                        {idx < timelinePhotos.length - 1 && (
                          <p className="text-[10px] text-[#8B5CF6] mt-2 font-medium">
                            {Math.round((new Date(photo.timestamp) - new Date(timelinePhotos[idx + 1].timestamp)) / (1000 * 60 * 60 * 24))} days since previous
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
